//changed due to package upgrade
import { useEffect, useState } from 'react';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
  alpha,
} from '@mui/material';
import jwtAxios from '@projectK/services/auth/JWT';
import AppScrollbar from '@projectK/components/AppScrollbar';
import { useIntl } from 'react-intl';
import AppLoader from '@projectK/components/AppLoader';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import AppCard from '@projectK/components/AppCard';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import AppInfoView from '@projectK/components/AppInfoView';
import { useTheme } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppsHeader from '@projectK/components/AppsHeader';
import '../../../../assets/styles/activities.css';
import SyncIcon from '@mui/icons-material/Sync';
import copy from 'copy-to-clipboard';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import {
  ButtonMain,
  MyScheduleMain,
  SearchFieldMain,
} from '@projectK/MainComponents';
import { useToast } from '@projectK/context/ToastProvider';
import {
  formatStartDate,
  timeZoneDate,
  formatTime,
} from '@projectK/modules/apps/DateFormat';
import { isParent } from '@projectK/context/RolesProvider';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

function getTruncatedAddressTitle(data) {
  const address = data?.address ?? '';
  const locationName = data?.location_name ?? '';
  return `${address}${locationName}`.substring(0, 50);
}
function getTruncatedResourceTitle(data) {
  return (data?.resource_name ?? '').substring(0, 50);
}

const isCancelabled = (data) => {
  return isStartDateAfterToday(data);
};
function isStartDateAfterToday(data) {
  const startDateString = data.start_date.substring(0, 10);
  const startTime = data.start_time;
  const startDateTimeString = `${startDateString}T${startTime}`;
  const startDate = timeZoneDate(startDateTimeString).startDate;
  const now = timeZoneDate().todayDate;
  return startDate > now;
}
const MySchedules = ({
  list,
  loading,
  setDateFilter,
  setSearchQuery,
  setShowAll,
  showAll,
  useDateFilter,
  fetchActivitySessions,
  fetchMySchedule,
  fetchUserProduct,
  setLoading,
}) => {//NOSONAR
  const { isMobile, mobileMode } = useMobileSettings();
  const showToast = useToast();
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  //google modal
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false);
  const companyTheme = CompanyTheme();
  const mobileResponse = useMediaQuery('(max-width:755px)');
  const theme = useTheme();
  const { messages } = useIntl();
  const { user } = useAuthUser();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [googleSyncStatus, setGoogleSyncStatus] = useState('idle');
  const [outlookSyncStatus, setOutlookSyncStatus] = useState('idle');

  const [lastSynced, setLastSynced] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [sessionToCancel, setSessionToCancel] = useState(null);
  const openModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const openConfirmDialog = (session) => {
    setSessionToCancel(session);
    setIsConfirmDialogOpen(true);
  };

  const closeGoogleModal = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
    setIsGoogleModalOpen(false);
  };

  const closeConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
    setSessionToCancel(null);
  };
  const onSearchActivity = (e) => {
    setSearchQuery(e.target.value);
  };

  const toggleShowAll = () => {
    setShowAll(!showAll);
    setDateFilter(!useDateFilter);
  };

  const handleCancelUp = async (userId, id, product_id) => {
    try {
      setLoading(true);
      // Make the Delete request using jwtAxios
      await jwtAxios.delete(`/user-session`, {
        params: {
          user_id: userId,
          activity_session_id: id,
          product_id: product_id,
        },
      });
      setLoading(false);
      fetchActivitySessions();
      fetchMySchedule();
      if (!isMobile) {
        fetchUserProduct();
        infoViewActionsContext.showMessage(`Cancelled session successfully.`);
      } else {
        showToast('Cancelled session successfully.', 'success', mobileMode);
      }
    const isParentUser = isParent(user);
        jwtAxios.get(
          `/events/update/user/calender`,
          isParentUser
            ? {
                params: {
                  user_id: userId,
                },
              }
            : {},
        );
      infoViewActionsContext.showMessage(`Cancelled session successfully`);
    } catch (error) {
      setLoading(false);
      if (isMobile) {
        showToast(error?.response?.data?.error, 'error', mobileMode);
      } else {
        infoViewActionsContext.fetchError(error?.response?.data?.error);
      }
    }
  };
  const handleCopyToClipboard = async () => {
    try {
      const response = await jwtAxios.get(`/events/calendar-feed-url/${user.id}`, {
        withCredentials: true,
      });
      const trimmedUrl = response.data?.url;
      if (!trimmedUrl) {
        infoViewActionsContext.fetchError('Unable to build calendar link');
        return;
      }
      copy(trimmedUrl);
      infoViewActionsContext.showMessage('Link copied successfully!');
    } catch (error) {
      infoViewActionsContext.fetchError(
        error?.response?.data?.error || 'Failed to copy calendar link',
      );
    }
  };

  const handleLink = async () => {
    try {
      const returnPath = '/dashboard';
      localStorage.setItem('calendarSyncInProgress', 'true');
      const response = await jwtAxios.get('/events/auth/google/initiate', {
        params: { returnPath },
        withCredentials: true,
      });

      const { url } = response.data;
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      console.error('Failed to initiate Google auth', err);
    }
  };

  const handleOutlookLink = async () => {
    try {
      const returnPath = '/dashboard';
      localStorage.setItem('calendarSyncInProgress', 'true');

      const response = await jwtAxios.get('/events/auth/outlook/initiate', {
        params: { returnPath },
        withCredentials: true,
      });

      const { url } = response.data;
      if (url) {
        window.location.href = url; // Redirect to Microsoft login
      }
    } catch (err) {
      console.error('Failed to initiate Outlook auth', err);
      showToast('Failed to connect Outlook calendar', 'error');
    }
  };

  const checkSyncStatus = async () => {
    try {
      const googleRes = await jwtAxios.get('/events/google/sync-status', {
        withCredentials: true,
      });
      const outlookRes = await jwtAxios.get('/events/outlook/sync-status', {
        withCredentials: true,
      });
      let googleStatus = googleRes.data.status;
      let outlookStatus = outlookRes.data.status;

      if (googleStatus === 'failed' && !googleRes.data.lastSynced) {
        googleStatus = 'idle';
      }
      if (outlookStatus === 'failed' && !outlookRes.data.lastSynced) {
        outlookStatus = 'idle';
      }

      setGoogleSyncStatus(googleStatus);
      setOutlookSyncStatus(outlookStatus);

      // Handle last synced time
      const latestSync =
        googleRes.data.lastSynced || outlookRes.data.lastSynced;
      if (latestSync) {
        const { startDate } = timeZoneDate(latestSync);
        setLastSynced(startDate.toFormat('MMM dd, yyyy hh:mm a'));
      }
    } catch (err) {
      console.error('Failed to check sync status', err);
      setGoogleSyncStatus('idle');
      setOutlookSyncStatus('idle');
    }
  };

  useEffect(() => {
    // On open, try to fetch initial sync status (if already connected)
    checkSyncStatus();
  }, [isModalOpen]);

  const responsiveSize = { xs: 12, md: 6, xl: 4 };
  const schedule = (
    <>
      {loading && <AppLoader />}
      {list?.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
          }}
        >
          <CalendarMonthIcon sx={{ fontSize: 40, marginBottom: 6 }} />
          <Typography variant='body1' sx={{ fontWeight: 'bold', fontSize: 18 }}>
            No upcoming schedules
          </Typography>
        </Box>
      ) : (
        <Grid
          container
          spacing={2}
          className={
            isMobile ? 'activity-session my-shedule' : 'activity-session'
          }
        >
          {list?.map((data) => {
            const truncatedAddressTitle = getTruncatedAddressTitle(data);
            const truncatedResourceTitle = getTruncatedResourceTitle(data);
            const isCancelable = isCancelabled(data);
            return (
              <Grid  key={data.id} size={{...responsiveSize}}>
                <MyScheduleMain
                  data={data}
                  user={user}
                  formatStartDate={formatStartDate}
                  formatTime={formatTime}
                  truncatedAddressTitle={truncatedAddressTitle}
                  truncatedResourceTitle={truncatedResourceTitle}
                  isCancelable={isCancelable}
                  companyTheme={companyTheme}
                  openConfirmDialog={openConfirmDialog}
                  theme={theme}
                  loading={loading}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </>
  );
  const buttonSize = mobileResponse ? 'small' : 'medium';
  const textColor = companyTheme?.primary?.main || 'primary';
  const borderCol = companyTheme?.primary?.main || '#2196f3';
  const borderHover = {
    borderColor: companyTheme
      ? alpha(companyTheme.primary.main, 0.8)
      : alpha('#2196f3', 0.8),
  };
  const minMaxwidth = mobileResponse ? { minWidth: 0 } : { minWidth: 100 };
  const viewText = showAll ? 'View All' : 'View Month';

  let connectionStatus = '';
  if (googleSyncStatus === 'success' && outlookSyncStatus === 'success') {
    connectionStatus = 'Connected to Google & Outlook Calendar';
  } else if (googleSyncStatus === 'success') {
    connectionStatus = 'Connected to Google Calendar';
  } else if (outlookSyncStatus === 'success') {
    connectionStatus = 'Connected to Outlook Calendar';
  } else {
    connectionStatus = 'Not Connected';
  }

  return (
    <AppCard ClassName='my-schedules'>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'row' : 'column',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'center' : 'flex-start',
          marginBottom: '10px',
          paddingLeft: '10px',
        }}
      >
        <Typography
          variant='h4'
          sx={{
            color: companyTheme ? companyTheme.primary.main : 'text.primary',
          }}
        >
          My Schedule
        </Typography>

        {isMobile && (
          <Box
            sx={{
              justifyContent: 'flex-end',
              gap: '10px',
            }}
          >
            <ButtonMain
              color='primary'
              fill='outline'
              size={buttonSize}
              style={{
                marginRight: '10px',
                borderColor: borderCol,
                color: textColor,
              }}
              sx={{
                position: 'relative',
                marginRight: '10px',
                ...minMaxwidth,
                borderColor: borderCol,
                '&:hover': borderHover,
                color: textColor,
              }}
              onClick={openModal}
              variant='outlined'
              name={
                googleSyncStatus === 'success' ||
                outlookSyncStatus === 'success' ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: 'green' }} />
                    {!mobileResponse && !isMobile && <>Connected to Calendar</>}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SyncIcon />
                    {!mobileResponse && !isMobile && 'Sync to Calendar'}
                  </Box>
                )
              }
            />
            <ButtonMain
              color='primary'
              fill='outline'
              size={buttonSize}
              style={{
                borderColor: borderCol,
                color: textColor,
              }}
              sx={{
                borderColor: borderCol,
                '&:hover': borderHover,
                color: textColor,
              }}
              onClick={toggleShowAll}
              variant='outlined'
              name={viewText}
            ></ButtonMain>
          </Box>
        )}
      </Box>

      <AppsHeader>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: 1,
          }}
        >
          <SearchFieldMain
            iconPosition='right'
            overlap={false}
            onChange={onSearchActivity}
            placeholder={messages['common.searchHere']}
          />
        </Box>

        {!isMobile && (
          <Box
            sx={{
              marginTop: '10px',
              marginBottom: '10px',
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Box
              sx={{
                justifyContent: 'flex-end',
                gap: '10px',
              }}
            >
              <ButtonMain
                color='primary'
                fill='outline'
                size={buttonSize}
                style={{
                  marginRight: '10px',
                  borderColor: borderCol,
                  color: textColor,
                }}
                sx={{
                  position: 'relative',
                  marginRight: '10px',
                  ...minMaxwidth,
                  borderColor: borderCol,
                  '&:hover': borderHover,
                  color: textColor,
                }}
                onClick={openModal}
                variant='outlined'
                name={
                  googleSyncStatus === 'success' ||
                  outlookSyncStatus === 'success' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: 'green' }} />
                      {!mobileResponse && <>Connected to Calendar</>}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SyncIcon />
                      {!mobileResponse && 'Sync to Calendar'}
                    </Box>
                  )
                }
              />
              <ButtonMain
                color='primary'
                fill='outline'
                size={buttonSize}
                style={{
                  borderColor: borderCol,
                  color: textColor,
                }}
                sx={{
                  borderColor: borderCol,
                  '&:hover': borderHover,
                  color: textColor,
                }}
                onClick={toggleShowAll}
                variant='outlined'
                name={viewText}
              ></ButtonMain>
            </Box>
          </Box>
        )}
      </AppsHeader>
      {isMobile ? (
        schedule
      ) : (
        <AppScrollbar className='scroll-products'>{schedule}</AppScrollbar>
      )}
      <AppInfoView />

      <Dialog open={isModalOpen} onClose={closeModal} fullWidth maxWidth='xs'>
        <DialogTitle>
          <Typography variant='h4'>Connect Your Calendar</Typography>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <Typography sx={{ mb: 3 }}>
            Sync your sessions to your personal calendar with one click. Once
            connected, all future bookings will automatically appear — no need
            to sync each time.
          </Typography>

          {/* Calendar Providers */}
          <Grid container spacing={1}>
            {/* Apple iCal */}
            <Grid size={{ xs: 12 }}>
              <Button
                fullWidth
                variant='outlined'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  fontWeight: 500,
                  color: '#202124',
                  borderColor: '#dadce0',
                  padding: '8px 12px',
                  '&:hover': {
                    borderColor: '#000',
                    backgroundColor: '#fafafa',
                  },
                }}
                onClick={handleCopyToClipboard}
              >
                {/* Left side: icon + text */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarMonthIcon
                    sx={{ fontSize: 35, mr: 2, color: '#5f6368' }}
                  />
                  <Typography>iCalendar</Typography>
                </Box>

                {/* Right side: open icon */}
                <OpenInNewIcon sx={{ fontSize: 20 }} />
              </Button>
            </Grid>

            {/* Google Calendar */}
              <Grid size={{ xs: 12 }}>
                <Button
                  fullWidth
                  variant='outlined'
                  onClick={handleLink}
                  disabled={googleSyncStatus === 'success'}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    fontWeight: 500,
                    color: '#202124',
                    borderColor: '#dadce0',
                    padding: '8px 12px',
                    '&:hover': {
                      borderColor: '#4285f4',
                      backgroundColor: '#f8faff',
                    },
                  }}
                >
                  {/* Left Section */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      component='img'
                      src='/assets/images/dashboard/calendar_30_2x.png'
                      alt='Google Calendar'
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'contain',
                        mr: 2,
                      }}
                    />
                    <Typography>Google Calendar</Typography>
                  </Box>

                {/* External link icon */}
                <OpenInNewIcon sx={{ fontSize: 20 }} />
              </Button>
            </Grid>

            {/* Outlook */}
            <Grid size={{ xs: 12 }}>
              <Button
                fullWidth
                variant='outlined'
                onClick={handleOutlookLink}
                disabled={outlookSyncStatus === 'success'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  fontWeight: 500,
                  color: '#202124',
                  borderColor: '#dadce0',
                  padding: '8px 12px',
                  backgroundColor: '#fafafa',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    component='img'
                    src='/assets/images/dashboard/outlook.jpeg'
                    alt='Outlook'
                    sx={{ width: 33, height: 33, objectFit: 'contain', mr: 2 }}
                  />
                  <Typography>Outlook Calendar</Typography>
                </Box>
                <OpenInNewIcon sx={{ fontSize: 20 }} />
              </Button>
            </Grid>
          </Grid>

          {/* Status Area */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: '#f9fafb',
              border: '1px solid #e0e0e0',
            }}
          >
            {/* Connecting */}
            {(googleSyncStatus === 'connecting' ||
              outlookSyncStatus === 'connecting') && (
              <Box display='flex' alignItems='center' gap={1}>
                <CircularProgress size={18} />
                <Typography>Connecting to Calendar...</Typography>
              </Box>
            )}

            {/* Connected */}
            {(googleSyncStatus === 'success' ||
              outlookSyncStatus === 'success') && (
              <>
                <Box display='flex' alignItems='center' gap={1}>
                  <CheckCircleIcon sx={{ color: 'green' }} />
                  <Typography fontWeight={600} color='green'>
                    {connectionStatus}
                  </Typography>
                </Box>
                <Typography variant='body2' sx={{ mt: 1 }}>
                  Your sessions are now syncing automatically.
                  <br />
                  Last synced: {lastSynced || 'Just now'}
                </Typography>
                <Box display='flex' gap={1} sx={{ mt: 2 }}>
                  {googleSyncStatus === 'success' && (
                    <Button variant='outlined' onClick={handleLink}>
                      Resync Google
                    </Button>
                  )}

                  {outlookSyncStatus === 'success' && (
                    <Button variant='outlined' onClick={handleOutlookLink}>
                      Resync Outlook
                    </Button>
                  )}
                </Box>
              </>
            )}

            {/* Failed */}
            {googleSyncStatus === 'failed' &&
              outlookSyncStatus === 'failed' && (
                <>
                  <Box display='flex' alignItems='center' gap={1}>
                    <ErrorIcon color='error' />
                    <Typography fontWeight={600} color='error'>
                      Sync Failed
                    </Typography>
                  </Box>
                  <Typography variant='body2' sx={{ mt: 1 }}>
                    We couldn’t verify your calendar connection. Please try
                    reconnecting.
                  </Typography>
                  <Button
                    variant='text'
                    sx={{ mt: 1 }}
                    onClick={checkSyncStatus}
                  >
                    Try Again
                  </Button>
                </>
              )}

            {/* Not Connected */}
            {googleSyncStatus === 'idle' && outlookSyncStatus === 'idle' && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                No calendar connected. Select any calendar above to get started.
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeModal} color='secondary'>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isConfirmDialogOpen} onClose={closeConfirmDialog}>
        <DialogTitle>
          <Typography variant='h4'>Confirm cancellation</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>
            Are you sure you want to cancel this session?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              !loading &&
                handleCancelUp(
                  sessionToCancel?.userId,
                  sessionToCancel?.id,
                  sessionToCancel?.product_id,
                );
              closeConfirmDialog();
            }}
            color='primary'
            autoFocus
          >
            Yes
          </Button>
          <Button onClick={closeConfirmDialog} color='secondary'>
            No
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={isGoogleModalOpen} onClose={closeGoogleModal} maxWidth='sm'>
        <DialogTitle>
          <Typography variant='h4'>Sync Confirmation</Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 'bold',
              color: successMessage ? 'green' : 'red',
            }}
          >
            {successMessage || errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeGoogleModal} color='primary'>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </AppCard>
  );
};

export default MySchedules;

MySchedules.propTypes = {
  list: PropTypes.array,
  loading: PropTypes.bool,
  setDateFilter: PropTypes.func,
  setSearchQuery: PropTypes.func,
  setShowAll: PropTypes.func,
  showAll: PropTypes.bool,
  useDateFilter: PropTypes.bool,
  fetchActivitySessions: PropTypes.func,
  fetchMySchedule: PropTypes.func,
  fetchUserProduct: PropTypes.func,
  setLoading: PropTypes.func,
};
